import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  path,
  ifElse,
  pipe,
  pathEq,
  T
} from 'ramda'
import { useQuery } from 'react-apollo'
import { useLocation, matchPath } from 'react-router-dom'

import Login from '.'
import PaymentView from '../rentals/payment-view'
import ConfirmationView from '../rentals/confirmation-view'
import CompleteAccountDetails from '../modals/complete-account-details'
import { useIsAuthenticated } from '../../hooks/use-is-authenticated'
import { UUID_SLUG } from '../../constants'
import CONTENT_ITEM_QUERY from '../../../graphql/queries/content-item.gql'
import { LoadingModal } from '../loading/modal'
import { useWelcomeBack } from '../subscriptions/neon/welcome-back/hooks/use-welcome-back'
import styles from './login-rental.css'

const parseLocationPathname = pathname => matchPath(pathname, {
  path: '/:slug/:screenId'
})

const getContentItemIdFromLocation = pipe(
  ifElse(
    path(['state', 'detailsUrl']),
    path(['state', 'detailsUrl']),
    path(['pathname'])
  ),
  parseLocationPathname,
  ifElse(
    pathEq(['params', 'slug'], UUID_SLUG),
    path(['params', 'screenId']),
    path(['url'])
  )
)

function LoginRental({
  onRequestClose
}) {
  const location = useLocation()
  const getInitialStep = ({ isAuthenticated, isDefaultProfileIncomplete }) => {
    if (!isAuthenticated) return 1
    if (isDefaultProfileIncomplete) return 99
    return 2
  }

  const isAuthenticated = useIsAuthenticated()
  const { isDefaultProfileIncomplete } = useWelcomeBack()

  const [step, setStep] = useState(
    getInitialStep({ isAuthenticated, isDefaultProfileIncomplete })
  )
  const [orderId, setOrderId] = useState('')
  const contentItemId = getContentItemIdFromLocation(location)

  const {
    data: contentItemQueryData,
    loading: contentItemQueryLoading,
    error: contentItemQueryError
  } = useQuery(CONTENT_ITEM_QUERY, {
    variables: {
      id: contentItemId
    },
    fetchPolicy: 'cache-first',
    skip: step === 1
  })

  if (contentItemQueryLoading || contentItemQueryError || !step) {
    return <LoadingModal />
  }

  const contentItem = contentItemQueryData?.contentItem

  const onPaymentSubmission = (newOrderId) => {
    setOrderId(newOrderId)
    setStep(prevStep => prevStep + 1)
  }

  const renderStep = (nextStep) => {
    setStep(nextStep)
  }

  function stepContent() {
    switch (step) {
      case 1:
        return (
          <Login />
        )
      case 2:
        return (
          <PaymentView
            contentItem={contentItem}
            onPaymentSubmission={onPaymentSubmission}
            onRequestClose={onRequestClose}
          />
        )
      case 3:
        return (
          <ConfirmationView
            contentItemId={contentItem.id}
            orderId={orderId}
            isAuthenticated={isAuthenticated}
          />
        )
      case 99:
        return (
          <CompleteAccountDetails renderStep={renderStep} isRental />
        )
      default:
        return null
    }
  }
  return (
    <div className={styles.wrapper}>
      {stepContent()}
    </div>
  )
}

LoginRental.propTypes = {
  onRequestClose: PropTypes.func
}

LoginRental.defaultProps = {
  onRequestClose: T
}

export default LoginRental
